/**
 * SettingsPanel — Slide-over panel for account, channels, and deal preferences.
 */

import { useEffect, useState, useCallback } from "react";
import {
  type SettingsProfile,
  type UserPreferences,
  getSettingsProfile,
  unlinkChannel,
  updatePreferences,
  createPortalSession,
  initiateTelegramLink,
} from "@/api/client";

const TIER_LABELS: Record<string, string> = {
  free: "Free",
  trial: "Trial",
  starter: "Starter",
  pro: "Pro",
};

const TIER_COLORS: Record<string, string> = {
  free: "#6b7280",
  trial: "#6b7280",
  starter: "#3b82f6",
  pro: "#8b5cf6",
};

const COUNTIES = ["", "Hamilton", "Marion", "Allen", "Lake"];
const PROPERTY_TYPES = ["", "Commercial", "Residential", "Industrial", "Agricultural"];

interface SettingsPanelProps {
  onClose: () => void;
}

export function SettingsPanel({ onClose }: SettingsPanelProps) {
  const [profile, setProfile] = useState<SettingsProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPricingTable, setShowPricingTable] = useState(false);
  const [prefsState, setPrefsState] = useState<UserPreferences>({});
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [unlinkingChannel, setUnlinkingChannel] = useState<string | null>(null);
  const [telegramLink, setTelegramLink] = useState<string | null>(null);
  const [linkingTelegram, setLinkingTelegram] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setTelegramLink(null);
    setLinkError(null);
    setLinkCopied(false);
    const data = await getSettingsProfile();
    if (data) {
      setProfile(data);
      setPrefsState(data.preferences || {});
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleUnlink = async (channel: string) => {
    setUnlinkingChannel(channel);
    const ok = await unlinkChannel(channel);
    if (ok) await loadProfile();
    setUnlinkingChannel(null);
  };

  const handleLinkTelegram = async () => {
    setLinkingTelegram(true);
    setLinkError(null);
    const result = await initiateTelegramLink();
    if (result) {
      setTelegramLink(result.deep_link);
    } else {
      setLinkError("Failed to generate link. Telegram may already be linked.");
    }
    setLinkingTelegram(false);
  };

  const handleSavePrefs = async () => {
    const ok = await updatePreferences(prefsState);
    if (ok) {
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2000);
    }
  };

  const handleManageSubscription = async () => {
    const url = await createPortalSession();
    if (url) window.location.href = url;
  };

  const formatTokens = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return String(n);
  };

  const tier = profile?.user?.tier || "free";
  const tierColor = TIER_COLORS[tier] || TIER_COLORS.free;
  const isPaid = tier === "starter" || tier === "pro";
  const billing = profile?.billing;
  const usagePct = billing && billing.daily_limit > 0
    ? Math.min(100, (billing.usage_today / billing.daily_limit) * 100)
    : 0;

  // Usage history sparkline bars
  const historyDays = profile?.usage_history || [];
  const maxTokens = Math.max(1, ...historyDays.map((d) => d.total_tokens));

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 900,
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(420px, 90vw)",
          background: "var(--bg-primary, #111)",
          borderLeft: "1px solid var(--border-primary, #2a2a2a)",
          zIndex: 901,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 20px",
            borderBottom: "1px solid var(--border-primary, #2a2a2a)",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 16, color: "var(--text-primary, #fff)" }}>
            Settings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--text-secondary, #888)",
              cursor: "pointer",
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            &times;
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {loading ? (
            <div style={{ color: "var(--text-secondary, #888)", fontSize: 13, textAlign: "center", marginTop: 40 }}>
              Loading...
            </div>
          ) : profile ? (
            <>
              {/* ── Account & Plan ──────────────────────────── */}
              <SectionHeader title="Account & Plan" />

              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span
                    style={{
                      background: tierColor,
                      color: "#fff",
                      padding: "2px 10px",
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {TIER_LABELS[tier] || tier}
                  </span>
                  <span style={{ color: "var(--text-secondary, #888)", fontSize: 12 }}>
                    {profile.user.email}
                  </span>
                </div>

                {/* Usage progress */}
                <div style={{ marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-secondary, #888)", marginBottom: 4 }}>
                    <span>Today: {formatTokens(billing?.usage_today || 0)}</span>
                    <span>Limit: {formatTokens(billing?.daily_limit || 0)}</span>
                  </div>
                  <div style={{ background: "var(--bg-tertiary, #1a1a1a)", borderRadius: 4, height: 6, overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${usagePct}%`,
                        height: "100%",
                        background: usagePct > 90 ? "#ef4444" : usagePct > 70 ? "#f59e0b" : tierColor,
                        borderRadius: 4,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>

                {/* 7-day usage history */}
                {historyDays.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, color: "var(--text-secondary, #888)", marginBottom: 6 }}>
                      7-day usage
                    </div>
                    <div style={{ display: "flex", alignItems: "end", gap: 3, height: 32 }}>
                      {historyDays
                        .slice()
                        .reverse()
                        .map((d, i) => {
                          const h = Math.max(2, (d.total_tokens / maxTokens) * 30);
                          return (
                            <div
                              key={i}
                              title={`${d.usage_date}: ${formatTokens(d.total_tokens)} (${d.message_count} msgs)`}
                              style={{
                                flex: 1,
                                height: h,
                                background: tierColor,
                                borderRadius: 2,
                                opacity: 0.7,
                                minWidth: 4,
                              }}
                            />
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setShowPricingTable((v) => !v)}
                    style={{
                      flex: 1,
                      padding: "7px 12px",
                      borderRadius: 6,
                      border: "none",
                      background: "#3b82f6",
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {showPricingTable ? "Hide Plans" : isPaid ? "Change Plan" : "Upgrade Plan"}
                  </button>
                  {isPaid && (
                    <button
                      onClick={handleManageSubscription}
                      style={{
                        flex: 1,
                        padding: "7px 12px",
                        borderRadius: 6,
                        border: "1px solid var(--border-primary, #2a2a2a)",
                        background: "none",
                        color: "var(--text-secondary, #ccc)",
                        cursor: "pointer",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Manage Subscription
                    </button>
                  )}
                </div>

                {/* Stripe Pricing Table (inline) */}
                {showPricingTable && (
                  <StripePricingTable customerEmail={profile.user.email} />
                )}
              </div>

              {/* ── Channels ──────────────────────────────── */}
              <SectionHeader title="Channels" />

              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {/* Web — always connected */}
                <ChannelCard
                  name="Web"
                  icon="🌐"
                  connected
                  detail="Always connected"
                />

                {/* Telegram */}
                {(() => {
                  const tg = profile.channels.find((c) => c.channel === "telegram");
                  return (
                    <ChannelCard
                      name="Telegram"
                      icon="✈️"
                      connected={!!tg}
                      detail={tg ? `Chat ${tg.channel_user_id}` : undefined}
                      linkedAt={tg?.linked_at}
                      onUnlink={tg ? () => handleUnlink("telegram") : undefined}
                      unlinking={unlinkingChannel === "telegram"}
                    >
                      {!tg && !telegramLink && (
                        <div style={{ marginTop: 8, marginLeft: 28 }}>
                          <button
                            onClick={handleLinkTelegram}
                            disabled={linkingTelegram}
                            style={{
                              padding: "6px 16px",
                              borderRadius: 6,
                              border: "none",
                              background: "#2AABEE",
                              color: "#fff",
                              cursor: linkingTelegram ? "default" : "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                              opacity: linkingTelegram ? 0.6 : 1,
                            }}
                          >
                            {linkingTelegram ? "Generating..." : "Link Telegram"}
                          </button>
                          {linkError && (
                            <div style={{ color: "#ef4444", fontSize: 11, marginTop: 4 }}>
                              {linkError}
                            </div>
                          )}
                        </div>
                      )}
                      {!tg && telegramLink && (
                        <div style={{ marginTop: 8, marginLeft: 28, display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <a
                              href={telegramLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-block",
                                padding: "6px 16px",
                                borderRadius: 6,
                                background: "#2AABEE",
                                color: "#fff",
                                fontSize: 12,
                                fontWeight: 600,
                                textDecoration: "none",
                                textAlign: "center",
                              }}
                            >
                              Open in Telegram
                            </a>
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(telegramLink);
                                setLinkCopied(true);
                                setTimeout(() => setLinkCopied(false), 2000);
                              }}
                              style={{
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "1px solid var(--border-primary, #2a2a2a)",
                                background: linkCopied ? "#10b981" : "var(--bg-tertiary, #1a1a1a)",
                                color: linkCopied ? "#fff" : "var(--text-secondary, #ccc)",
                                cursor: "pointer",
                                fontSize: 12,
                                fontWeight: 600,
                                transition: "background 0.2s, color 0.2s",
                              }}
                            >
                              {linkCopied ? "Copied!" : "Copy Link"}
                            </button>
                          </div>
                          <span style={{ fontSize: 11, color: "var(--text-secondary, #888)" }}>
                            Link expires in 10 minutes
                          </span>
                          <button
                            onClick={loadProfile}
                            style={{
                              background: "none",
                              border: "1px solid var(--border-primary, #2a2a2a)",
                              color: "var(--text-secondary, #ccc)",
                              cursor: "pointer",
                              fontSize: 11,
                              padding: "4px 12px",
                              borderRadius: 4,
                              alignSelf: "flex-start",
                            }}
                          >
                            I've linked it — refresh
                          </button>
                        </div>
                      )}
                    </ChannelCard>
                  );
                })()}

                {/* SMS */}
                <ChannelCard
                  name="SMS"
                  icon="💬"
                  connected={false}
                  instructions="Coming soon"
                />
              </div>

              {/* ── Deal Preferences ──────────────────────── */}
              <SectionHeader title="Deal Preferences" />

              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                <SelectField
                  label="Default County"
                  value={prefsState.default_county || ""}
                  options={COUNTIES}
                  onChange={(v) => setPrefsState((p) => ({ ...p, default_county: v || undefined }))}
                />
                <SelectField
                  label="Default Property Type"
                  value={prefsState.default_property_type || ""}
                  options={PROPERTY_TYPES}
                  onChange={(v) => setPrefsState((p) => ({ ...p, default_property_type: v || undefined }))}
                />

                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <button
                    onClick={handleSavePrefs}
                    style={{
                      padding: "7px 20px",
                      borderRadius: 6,
                      border: "none",
                      background: "#10b981",
                      color: "#fff",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Save Preferences
                  </button>
                  {prefsSaved && (
                    <span style={{ color: "#10b981", fontSize: 12 }}>Saved!</span>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ color: "#ef4444", fontSize: 13, textAlign: "center", marginTop: 40 }}>
              Failed to load settings.
            </div>
          )}
        </div>
      </div>

    </>
  );
}

// ── Sub-components ──────────────────────────────────────────

/** Stripe Pricing Table — loads the Stripe JS script and renders the web component inline. */
function StripePricingTable({ customerEmail }: { customerEmail: string }) {
  const tableId = import.meta.env.VITE_STRIPE_PRICING_TABLE_ID || "";
  const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || "";

  useEffect(() => {
    // Load the Stripe pricing-table.js script once
    if (!document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]')) {
      const script = document.createElement("script");
      script.src = "https://js.stripe.com/v3/pricing-table.js";
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  if (!tableId || !pubKey) {
    return (
      <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: "1px solid var(--border-primary, #2a2a2a)", background: "var(--bg-secondary, #1a1a1a)", fontSize: 12, color: "var(--text-secondary, #888)" }}>
        Pricing table not configured. Set <code>VITE_STRIPE_PRICING_TABLE_ID</code> and <code>VITE_STRIPE_PUBLISHABLE_KEY</code> in your environment.
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      {/* @ts-expect-error — Stripe web component, not in React's JSX typings */}
      <stripe-pricing-table
        pricing-table-id={tableId}
        publishable-key={pubKey}
        customer-email={customerEmail}
      />
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: "var(--text-secondary, #888)",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        marginBottom: 10,
        paddingBottom: 6,
        borderBottom: "1px solid var(--border-primary, #2a2a2a)",
      }}
    >
      {title}
    </div>
  );
}

function ChannelCard({
  name,
  icon,
  connected,
  detail,
  linkedAt,
  onUnlink,
  unlinking,
  instructions,
  children,
}: {
  name: string;
  icon: string;
  connected: boolean;
  detail?: string;
  linkedAt?: string;
  onUnlink?: () => void;
  unlinking?: boolean;
  instructions?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: "10px 12px",
        borderRadius: 8,
        border: "1px solid var(--border-primary, #2a2a2a)",
        background: "var(--bg-secondary, #1a1a1a)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>{icon}</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary, #fff)" }}>
            {name}
          </span>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: connected ? "#10b981" : "#4b5563",
              display: "inline-block",
            }}
          />
        </div>
        {onUnlink && (
          <button
            onClick={onUnlink}
            disabled={unlinking}
            style={{
              background: "none",
              border: "none",
              color: "#ef4444",
              cursor: unlinking ? "default" : "pointer",
              fontSize: 11,
              opacity: unlinking ? 0.5 : 1,
            }}
          >
            {unlinking ? "Unlinking..." : "Unlink"}
          </button>
        )}
      </div>
      {detail && (
        <div style={{ fontSize: 12, color: "var(--text-secondary, #888)", marginTop: 4, marginLeft: 28 }}>
          {detail}
          {linkedAt && ` · linked ${new Date(linkedAt).toLocaleDateString()}`}
        </div>
      )}
      {instructions && (
        <div style={{ fontSize: 11, color: "var(--text-secondary, #888)", marginTop: 6, marginLeft: 28, fontStyle: "italic" }}>
          {instructions}
        </div>
      )}
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label style={{ fontSize: 12, color: "var(--text-secondary, #888)", display: "block", marginBottom: 4 }}>
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          padding: "6px 8px",
          borderRadius: 6,
          border: "1px solid var(--border-primary, #2a2a2a)",
          background: "var(--bg-secondary, #1a1a1a)",
          color: "var(--text-primary, #fff)",
          fontSize: 13,
          outline: "none",
        }}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt || "— None —"}
          </option>
        ))}
      </select>
    </div>
  );
}
